import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import ContactLeadModal from '../../components/public/ContactLeadModal';
import VehicleCardPublic from '../../components/website/public/VehicleCardPublic';
import { Vehicle } from '../../components/website/WebsiteTemplate';
import { apiService } from '../../services/api';
import { usePublicSitePath } from './publicSitePaths';
import { HomeExtendedCopy, PublicLanguage } from './types';

interface HomePageProps {
  title: string;
  subtitle: string;
  ctaInventory: string;
  ctaApply: string;
  featuredTitle: string;
  detailCta: string;
  noVehicles: string;
  vehicles: Vehicle[];
  language: PublicLanguage;
  contactPhone: string;
  contactTextPhone?: string;
  contactWhatsappPhone?: string;
  bhphBadgeImageUrl?: string;
  dealershipName?: string;
  homeExtended: HomeExtendedCopy;
}

const getVisibleCards = () => {
  if (typeof window === 'undefined') {
    return 1;
  }

  if (window.innerWidth >= 1280) {
    return 4;
  }

  if (window.innerWidth >= 640) {
    return 2;
  }

  return 1;
};

const HomePage: React.FC<HomePageProps> = ({
  ctaInventory,
  ctaApply,
  featuredTitle,
  detailCta,
  noVehicles,
  vehicles,
  language,
  contactPhone,
  contactTextPhone,
  contactWhatsappPhone,
  bhphBadgeImageUrl,
  dealershipName,
  homeExtended,
}) => {
  const publicSitePath = usePublicSitePath();
  const callPhone = contactPhone;
  const callHref = `tel:${callPhone.replace(/[^+\d]/g, '')}`;
  const textPhone = contactTextPhone || contactPhone;
  const whatsappPhone = contactWhatsappPhone || textPhone;
  const badgeImageUrl = bhphBadgeImageUrl ? apiService.getFileUrl(bhphBadgeImageUrl) : undefined;
  const name = dealershipName || 'Our Dealership';

  // Replace {{phone}} and {{dealership}} placeholders with tenant values
  const sub = (s: string) =>
    s.replace(/\{\{phone\}\}/g, callPhone).replace(/\{\{dealership\}\}/g, name);
  const c = {
    ...homeExtended,
    ticker: homeExtended.ticker.map(sub),
    socialProof: sub(homeExtended.socialProof),
    steps: homeExtended.steps.map((s) => ({ ...s, body: sub(s.body) })),
    stepsBtn: sub(homeExtended.stepsBtn),
    whyTitle: sub(homeExtended.whyTitle),
    whyBullets: homeExtended.whyBullets.map(sub),
    whyBtn: sub(homeExtended.whyBtn),
  };
  const featuredVehicles = vehicles;
  const tickerContent = [...c.ticker, ...c.ticker];
  const [visibleCards, setVisibleCards] = useState(1);
  const [activePage, setActivePage] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [textLeadOpen, setTextLeadOpen] = useState(false);

  useEffect(() => {
    const syncVisibleCards = () => {
      setVisibleCards(getVisibleCards());
    };

    syncVisibleCards();
    window.addEventListener('resize', syncVisibleCards);

    return () => {
      window.removeEventListener('resize', syncVisibleCards);
    };
  }, []);

  const featuredPages: Vehicle[][] = [];
  for (let index = 0; index < featuredVehicles.length; index += visibleCards) {
    featuredPages.push(featuredVehicles.slice(index, index + visibleCards));
  }

  const pageCount = featuredPages.length;

  useEffect(() => {
    setActivePage((currentPage) => Math.min(currentPage, Math.max(pageCount - 1, 0)));
  }, [pageCount]);

  useEffect(() => {
    if (pageCount <= 1 || isCarouselPaused) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setActivePage((currentPage) => (currentPage + 1) % pageCount);
    }, 4500);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isCarouselPaused, pageCount]);

  const goToPreviousPage = () => {
    setActivePage((currentPage) => (currentPage - 1 + pageCount) % pageCount);
  };

  const goToNextPage = () => {
    setActivePage((currentPage) => (currentPage + 1) % pageCount);
  };

  return (
    <>
      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-track {
          display: flex;
          width: max-content;
          animation: ticker-scroll 28s linear infinite;
        }
        .ticker-track:hover { animation-play-state: paused; }
        .oswald { font-family: 'Oswald', sans-serif; }
      `}</style>

      {/* ── Ticker ── */}
      <div className="bg-blue-800 overflow-hidden py-2" style={{ backgroundColor: '#1D4ED8' }}>
        <div className="ticker-track">
          {tickerContent.map((item, i) => (
            <span
              key={i}
              className="oswald text-white text-sm font-medium whitespace-nowrap mx-8 tracking-wide"
            >
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ── Hero ── */}
      <section
        className="relative min-h-[520px] flex items-center"
        style={{
          backgroundImage: 'url("/images/hero-bg.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center 60%',
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(10,22,40,0.92) 0%, rgba(29,78,216,0.75) 60%, rgba(220,38,38,0.70) 100%)',
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              <span className="text-green-400 text-sm font-bold oswald tracking-widest uppercase">
                {vehicles.length > 0
                  ? c.vehiclesAvailable(vehicles.length)
                  : c.vehiclesAvailable(23)}
              </span>
            </div>

            <h1 className="oswald text-5xl md:text-7xl font-bold text-white leading-tight mb-4">
              {c.heroHeadline[0]}
              <br />
              <span className="text-red-400">{c.heroHeadline[1]}</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-6">
              {c.heroSub[0]}
              <span className="text-white font-bold">{c.heroSub[1]}</span>
              {c.heroSub[2]}
            </p>

            <div className="flex flex-wrap gap-2 mb-8">
              {c.approvalBadges.map((badge) => (
                <span
                  key={badge}
                  className="bg-white/10 border border-white/30 text-white text-xs font-semibold px-3 py-1 rounded-full backdrop-blur-sm"
                >
                  {badge} — <span className="text-green-400">APPROVED</span>
                </span>
              ))}
            </div>

            <p className="text-base text-gray-300 mb-6">
              {c.paymentLine[0]}
              <span className="text-white font-bold">{c.paymentLine[1]}</span>
              {c.paymentLine[2]}
              <span className="text-yellow-400 font-bold">{c.paymentLine[3]}</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={callHref}
                className="oswald inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 hover:bg-red-700 px-8 py-4 text-xl font-bold text-white transition-colors shadow-2xl tracking-wide"
              >
                📞 {language === 'es' ? 'Llamar' : 'Call'} {callPhone}
              </a>
              <Link
                to={publicSitePath('/financing')}
                className="oswald inline-flex items-center justify-center rounded-xl bg-yellow-400 hover:bg-yellow-300 px-8 py-4 text-xl font-bold text-gray-900 transition-colors shadow-xl tracking-wide"
              >
                {ctaApply} — {language === 'es' ? 'Toma 2 Min ›' : 'Takes 2 Min ›'}
              </Link>
            </div>

            <p className="mt-4 text-gray-400 text-sm">{c.socialProof}</p>
          </div>
        </div>
      </section>

      {/* ── Featured Vehicles ── */}
      <section className="bg-gray-50 py-14">
        <div className="w-full">
          <div className="mb-8 flex flex-col gap-4 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
            <div>
              <h2 className="oswald text-3xl md:text-4xl font-bold text-gray-900 tracking-wide">
                {featuredTitle}
              </h2>
              {pageCount > 1 && (
                <p className="mt-2 text-sm text-gray-500">
                  {language === 'es'
                    ? 'Carrusel automático. Pausa al pasar el cursor.'
                    : 'Auto-rotating carousel. Pauses on hover.'}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {pageCount > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={goToPreviousPage}
                    aria-label={
                      language === 'es' ? 'Ver vehículos anteriores' : 'Show previous vehicles'
                    }
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm transition-colors hover:border-blue-700 hover:text-blue-700"
                  >
                    <span aria-hidden="true">&larr;</span>
                  </button>
                  <button
                    type="button"
                    onClick={goToNextPage}
                    aria-label={language === 'es' ? 'Ver más vehículos' : 'Show more vehicles'}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 shadow-sm transition-colors hover:border-blue-700 hover:text-blue-700"
                  >
                    <span aria-hidden="true">&rarr;</span>
                  </button>
                </div>
              )}
              <Link
                to={publicSitePath('/inventory')}
                className="text-blue-700 hover:text-blue-900 text-sm font-semibold border border-blue-700 hover:border-blue-900 rounded-lg px-4 py-2 transition-colors"
              >
                {ctaInventory} →
              </Link>
            </div>
          </div>
          {featuredVehicles.length === 0 ? (
            <div className="mx-4 rounded-xl border border-gray-200 bg-white p-12 text-center text-gray-500 sm:mx-6 lg:mx-8">
              {noVehicles}
            </div>
          ) : (
            <div
              className="w-full border-y border-gray-200 bg-white px-4 py-6 shadow-sm sm:px-6 lg:px-8"
              onMouseEnter={() => setIsCarouselPaused(true)}
              onMouseLeave={() => setIsCarouselPaused(false)}
            >
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-700 ease-out"
                  style={{ transform: `translateX(-${activePage * 100}%)` }}
                >
                  {featuredPages.map((page, pageIndex) => (
                    <div
                      key={pageIndex}
                      className="grid min-w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4"
                    >
                      {page.map((vehicle) => (
                        <VehicleCardPublic
                          key={vehicle.id}
                          vehicle={vehicle}
                          ctaLabel={detailCta}
                          callPhone={callPhone}
                          textPhone={textPhone}
                          whatsappPhone={whatsappPhone}
                          badgeImageUrl={badgeImageUrl}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {pageCount > 1 && (
                <div className="mt-6 flex items-center justify-center gap-2">
                  {featuredPages.map((_, pageIndex) => (
                    <button
                      key={pageIndex}
                      type="button"
                      onClick={() => setActivePage(pageIndex)}
                      aria-label={
                        language === 'es'
                          ? `Ir al grupo ${pageIndex + 1}`
                          : `Go to vehicle group ${pageIndex + 1}`
                      }
                      className={`h-2.5 rounded-full transition-all ${pageIndex === activePage ? 'w-10 bg-blue-700' : 'w-2.5 bg-gray-300 hover:bg-gray-400'}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── How It Works / Value Props ── */}
      <section className="py-14 px-4 sm:px-6 lg:px-8 bg-gray-50 border-t border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-red-600 font-bold text-sm uppercase tracking-widest mb-2">
              {c.howItWorksSub}
            </p>
            <h2 className="oswald text-3xl sm:text-4xl font-bold text-gray-900 tracking-wide uppercase">
              {c.bhphTitle}
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">{c.bhphDesc}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {c.cards.map((card, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-all text-center group ${i === 0 ? 'hover:border-red-200' : i === 1 ? 'hover:border-blue-200' : 'hover:border-yellow-300'}`}
              >
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 transition-colors ${i === 0 ? 'bg-red-100 group-hover:bg-red-600' : i === 1 ? 'bg-blue-100 group-hover:bg-blue-700' : 'bg-yellow-100 group-hover:bg-yellow-400'}`}
                >
                  <span className="text-3xl">{card.emoji}</span>
                </div>
                <h3 className="oswald text-2xl font-bold text-gray-900 mb-3 tracking-wide uppercase">
                  {card.title}
                </h3>
                <ul className="space-y-2 text-gray-600 text-sm text-left">
                  {card.items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-green-500 font-bold">✅</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3-Step Process ── */}
      <section className="py-14 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1D4ED8' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="oswald text-3xl sm:text-4xl font-bold text-white uppercase tracking-wide">
              {c.stepsTitle}
            </h2>
            <p className="text-blue-200 mt-2">{c.stepsSub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {c.steps.map(({ step, title, body }) => (
              <div key={step} className="text-center">
                <div className="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl">
                  <span className="oswald text-3xl font-bold text-gray-900">{step}</span>
                </div>
                <h3 className="oswald text-xl font-bold text-white uppercase mb-2 tracking-wide">
                  {title}
                </h3>
                <p className="text-blue-200 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <a
              href={callHref}
              className="oswald inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-10 rounded-xl text-xl tracking-wide transition-colors shadow-2xl"
            >
              {c.stepsBtn}
            </a>
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="bg-red-600 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="oswald text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-3 tracking-wide">
            {c.ctaBannerTitle}
          </h2>
          <p className="text-red-100 text-lg mb-8">{c.ctaBannerSub}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={callHref}
              className="oswald inline-flex items-center justify-center gap-2 rounded-xl bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-xl font-bold transition-colors tracking-wide shadow-lg"
            >
              📞 {callPhone}
            </a>
            <Link
              to={publicSitePath('/financing')}
              className="oswald inline-flex items-center justify-center gap-2 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-8 py-4 text-xl font-bold transition-colors tracking-wide shadow-xl"
            >
              {c.ctaBannerApply}
            </Link>
            <button
              type="button"
              onClick={() => setTextLeadOpen(true)}
              className="oswald inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white text-white hover:bg-white/10 px-8 py-4 text-xl font-bold transition-colors tracking-wide"
            >
              💬 {language === 'es' ? 'Texto' : 'Text'} {textPhone}
            </button>
          </div>
          <p className="text-red-200 text-xs mt-4">{c.ctaBannerFooter}</p>
        </div>
      </section>

      {/* ── Why Us / Trust ── */}
      <section className="bg-white py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="oswald text-3xl md:text-4xl font-bold text-gray-900 mb-6 tracking-wide">
                {c.whyTitle}
              </h2>
              <div className="grid grid-cols-3 gap-4 mb-8">
                {c.stats.map(({ value, label }) => (
                  <div key={label} className="text-center p-4 bg-gray-50 rounded-xl">
                    <div className="oswald text-2xl font-bold text-blue-700">{value}</div>
                    <div className="text-xs text-gray-600 mt-1">{label}</div>
                  </div>
                ))}
              </div>
              <ul className="space-y-3 text-gray-700">
                {c.whyBullets.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="text-green-500 font-bold text-lg mt-0.5">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href={callHref}
                className="oswald mt-8 inline-flex items-center gap-2 rounded-xl bg-blue-700 hover:bg-blue-800 px-6 py-3 text-lg font-bold text-white transition-colors tracking-wide"
              >
                {c.whyBtn}
              </a>
            </div>

            <div className="space-y-4">
              {c.reviews.map((review) => (
                <div key={review.name} className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-full bg-blue-700 flex items-center justify-center text-white font-bold text-sm">
                      {review.name[0]}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">{review.name}</div>
                      <div className="text-yellow-500 text-xs">{'★'.repeat(review.rating)}</div>
                    </div>
                    <span className="ml-auto text-xs text-gray-400">Google Review</span>
                  </div>
                  <p className="text-gray-700 text-sm italic">{review.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ContactLeadModal
        open={textLeadOpen}
        channel="text"
        onClose={() => setTextLeadOpen(false)}
        contactPhone={textPhone}
      />
    </>
  );
};

export default HomePage;
